/* Fast Array.from for webOS 3 (Chromium 38) and every other legacy-bundle browser.

   Chrome 38 has no native Array.from, so the legacy polyfills install core-js's.
   It stores every element through core-js's createProperty -> its Symbol-shim
   defineProperty wrapper (property-key coercion + hidden-key bookkeeping +
   a real defineProperty per index): measured on the TV, Array.from of a
   1000-char string takes 127ms vs 4ms for a plain loop (~30x). Babel's spread
   helper calls Array.from for every non-array iterable (`[...string]`,
   `[...set]`, `[...map.keys()]`), and the JWT decode in @dtvgo/utils does
   `[...atob(payload)]`: the token check that runs on every private route change
   and before each authorizer call cost 3-5s of every channel join (profiled on
   ch48, both players).

   Installs a push-based Array.from for the plain cases (receiver Array, strings,
   iterables, array-likes) and hands anything unusual (subclass receivers, a bad
   mapFn, null) to core-js, so errors and exotic semantics stay core-js's. Legacy
   build only: a modern bundle keeps the native Array.from. Imported from
   src/index.jsx with the other fast shims. */

// eslint-disable-next-line no-underscore-dangle -- marker property, mirrors fastObjectAssign
const CoreFrom = typeof Array.from === 'function' && !Array.from.__dtvFastArrayFrom
  ? Array.from
  : undefined;
const ITERATOR = typeof Symbol === 'function' ? Symbol.iterator : undefined;

/* charCodeAt, not codePointAt: Chrome 38 has no native codePointAt, and
   Reflect.apply would hit the core-js shim too. */
/* eslint-disable unicorn/prefer-code-point, unicorn/prefer-reflect-apply -- see above */

/* String iteration is by code point: a surrogate pair is one element. */
const fromString = (string, mapper) => {
  const result = [];
  const { length } = string;
  for (let index = 0; index < length; index += 1) {
    let char = string.charAt(index);
    const code = string.charCodeAt(index);
    if (code >= 0xD8_00 && code <= 0xDB_FF && index + 1 < length) {
      const next = string.charCodeAt(index + 1);
      if (next >= 0xDC_00 && next <= 0xDF_FF) {
        char += string.charAt(index + 1);
        index += 1;
      }
    }
    result.push(mapper ? mapper(char, result.length) : char);
  }
  return result;
};

const fromIterator = (iterator, mapper) => {
  const result = [];
  for (let step = iterator.next(); !step.done; step = iterator.next()) {
    if (!mapper) {
      result.push(step.value);
      continue;
    }
    try {
      result.push(mapper(step.value, result.length));
    } catch (error) {
      /* IteratorClose, as the spec does on an abrupt mapFn. */
      if (typeof iterator.return === 'function') iterator.return();
      throw error;
    }
  }
  return result;
};

const fromArrayLike = (items, mapper) => {
  const result = [];
  const length = Math.min(Math.max(Math.floor(Number(items.length)) || 0, 0), 2 ** 53 - 1);
  for (let index = 0; index < length; index += 1) {
    result.push(mapper ? mapper(items[index], index) : items[index]);
  }
  return result;
};

if (import.meta.env.LEGACY && CoreFrom) {
  // eslint-disable-next-line func-names -- keeps Array.from's own name
  const fastFrom = function from(items, mapFn, thisArg) {
    if ((this !== Array && this !== undefined)
      || items === undefined || items === null
      || (mapFn !== undefined && typeof mapFn !== 'function')) {
      // eslint-disable-next-line prefer-rest-params -- forward the call untouched
      return CoreFrom.apply(this, arguments);
    }
    const mapper = mapFn ? (value, index) => mapFn.call(thisArg, value, index) : undefined;
    if (typeof items === 'string') return fromString(items, mapper);
    const getIterator = (ITERATOR && items[ITERATOR]) || items['@@iterator'];
    if (typeof getIterator === 'function') return fromIterator(getIterator.call(items), mapper);
    return fromArrayLike(items, mapper);
  };
  // eslint-disable-next-line no-underscore-dangle -- marker property, mirrors fastObjectAssign
  fastFrom.__dtvFastArrayFrom = true;
  Object.defineProperty(Array, 'from', {
    configurable: true, enumerable: false, writable: true, value: fastFrom,
  });
}

/* eslint-enable unicorn/prefer-code-point, unicorn/prefer-reflect-apply -- end of block */
